import {DocumentNode} from 'graphql';
import gql from 'graphql-tag';
import React from 'react';

import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import {WorklistItemsQuery, WorklistItemsQueryVariables} from 'worklists/__generated/WorklistItemsQuery';
import {
  ListItemValidation,
  SelectableItemsTable,
  SelectItemsAdditionalColumn,
  SelectItemsFromListView,
} from 'worklists/SelectItemsFromListView';
import {WorklistSummary, WorklistType} from 'worklists/WorklistConstants';
import WorklistSearchInput from 'worklists/WorklistSearchInput';

export type WorklistItemNode = ExtractByTypename<
  WorklistItemsQuery['worklist'],
  'WorklistNode'
>['items'][number];

export const WORKLIST_ITEMS_CONTAINER_FRAGMENT = gql`
  fragment WorklistItemContainerFragment on ContainerNode {
    id
    name
    barcode {
      value
    }
    contents {
      containedItem {
        ... on FolderItemInterface {
          id
        }
        ...InaccessibleInfoGraphQL
      }
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export const WORKLIST_ITEMS_QUERY = gql`
  query WorklistItemsQuery($worklistId: StrictString!) @skipAudit @engComponent(name: WORKLISTS) {
    worklist(id: $worklistId) {
      ... on WorklistNode {
        id
        items {
          ... on BatchNode {
            id
            name
          }
          ... on PlateNode {
            id
            name
          }
          ... on ContainerNode {
            ...WorklistItemContainerFragment
          }
          ... on FolderItemInterface {
            id
            name
          }
          ... on InaccessibleInfoNode {
            ...InaccessibleInfoGraphQL
          }
        }
      }
      ... on InaccessibleInfoNode {
        ...InaccessibleInfoGraphQL
      }
    }
  }
  ${WORKLIST_ITEMS_CONTAINER_FRAGMENT}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

interface SelectItemsFromWorklistViewProps<TWorklistItemNode extends WorklistItemNode> {
  selectedWorklistItems: Array<TWorklistItemNode>;
  setSelectedWorklistItems: (worklistItems: Array<TWorklistItemNode>) => void;
  setWorklistSummary: (worklistSummary: WorklistSummary | null) => void;
  validateItem: (worklistItemNode: TWorklistItemNode) => ListItemValidation;
  worklistSummary: WorklistSummary | null;
  query?: DocumentNode | null;
  additionalColumns?: Array<SelectItemsAdditionalColumn<TWorklistItemNode>>;
}

// A query can be passed to use a non-standard query to fetch worklist items. Otherwise, the query in this
//  file is used.
export function SelectItemsFromWorklistView<TWorklistItemsQuery extends WorklistItemsQuery>({
  selectedWorklistItems,
  setSelectedWorklistItems,
  setWorklistSummary,
  validateItem,
  worklistSummary,
  query,
  additionalColumns,
}: SelectItemsFromWorklistViewProps<
  ExtractByTypename<TWorklistItemsQuery['worklist'], 'WorklistNode'>['items'][number]
>): JSX.Element {
  const searchInput = (
    <WorklistSearchInput
      onChange={(worklist) => {
        setWorklistSummary(worklist);
        if (worklist == null) {
          setSelectedWorklistItems([]);
        }
      }}
      worklistTypes={[WorklistType.BIOENTITY, WorklistType.CONTAINER]}
    />
  );

  return (
    <SelectItemsFromListView<TWorklistItemsQuery, WorklistSummary, WorklistItemsQueryVariables>
      listSummary={worklistSummary}
      query={query ?? WORKLIST_ITEMS_QUERY}
      queryVariablesFromListSummary={(worklistSummary: WorklistSummary) => ({worklistId: worklistSummary.id})}
      listSearchInput={searchInput}
    >
      {(data): JSX.Element => {
        const worklist = data.worklist;
        if (isInaccessible(worklist)) {
          return (
            <InlineBanner
              bannerType={InlineBannerType.WARNING}
              text="You don't have permissions to view this worklist"
            />
          );
        } else if (worklist.items.length === 0) {
          return <InlineBanner bannerType={InlineBannerType.INFO} text="This worklist is empty" />;
        } else {
          return (
            <SelectableItemsTable<
              ExtractByTypename<TWorklistItemsQuery['worklist'], 'WorklistNode'>['items'][number]
            >
              items={worklist.items}
              selectedItems={selectedWorklistItems}
              setSelectedItems={setSelectedWorklistItems}
              validateItem={validateItem}
              additionalColumns={additionalColumns}
            />
          );
        }
      }}
    </SelectItemsFromListView>
  );
}
